import React, { useState, useRef } from 'react';
import Select from 'react-select';

import { validateEmailAddress, validatePhoneNumber } from '../../../../../util/ValidationUtil';

import { deleteRelation } from '../../../../../services/ClientService';

import Modal from '../../../../common/modal/Modal';
import { ToolTip } from '../../../../common/tool-tip/ToolTip';
import { SaveButton } from '../../../../common/button/save-button/SaveButton';
import { Checkbox } from '../../../../common/checkbox/Checkbox';

import './ClientsRelationsSearch.css';

export const ClientRelations = ({
  relation,
  relations,
  discretionList,
  context,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
  closeModal,
  updateSearchResults,
  clientRelationInformation,
  clientRelationError,
  setClientRelationError,
  handleClientRelationInformationChange,
  disableMobileInput,
  setClient,
  fieldsDisabled,
}) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const changeMainContact = useRef(undefined);

  const handleConfirmationModalYes = () => {
    changeMainContact.current = true;
    saveRelation();
  };

  const handleConfirmationModalNo = () => {
    changeMainContact.current = false;
    saveRelation();
  };

  const handleDeleteClick = async () => {
    const copyOfRelations = [...relations];

    if (relation.externalReference) {
      const [data, error] = await deleteRelation(context.username, relation.externalReference);
      if (data) {
        if (data.responseStatus === 'Success') {
          const newRelations = copyOfRelations.filter((relation) => !relation.externalReference);

          setClient((prev) => ({
            ...prev,
            locatorInformation: {
              ...prev.locatorInformation,
              relations: [...data.clientVO.locatorInformation.relations, ...newRelations],
              contactDetails: data.clientVO.locatorInformation.contactDetails,
            },
          }));
        } else {
          showToaster(data);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }

      setSpinnerText(null);
      closeModal();
    } else {
      const relationIndex = copyOfRelations.findIndex((relationInList) => {
        if (relationInList.firstName !== relation.firstName) return false;
        if (relationInList.lastName !== relation.lastName) return false;
        if (relationInList.relationship !== relation.relationship) return false;
        if (relationInList.mobile !== relation.mobile) return false;
        if (relationInList.email !== relation.email) return false;

        return true;
      });

      copyOfRelations.splice(relationIndex, 1);
      updateSearchResults(copyOfRelations);
      closeModal();
    }
  };

  const handleSaveRelationInModal = () => {
    let mobileNumberError = '';

    if (clientRelationInformation.relationMobile) {
      if (!validatePhoneNumber(clientRelationInformation.relationMobile)) {
        mobileNumberError = 'Number must be 10 digits';
      }
    } else {
      mobileNumberError = '*REQUIRED';
    }

    let emailError = '';

    if (clientRelationInformation.relationEmail) {
      if (!validateEmailAddress(clientRelationInformation.relationEmail)) {
        emailError = 'Invalid email address';
      }
    }

    setClientRelationError({
      firstNameError: !clientRelationInformation.relationFirstName ? '*REQUIRED' : '',
      lastNameError: !clientRelationInformation.relationLastName ? '*REQUIRED' : '',
      relationshipError: !clientRelationInformation.relationship ? '*REQUIRED' : '',
      mobileNumberError: mobileNumberError,
      emailError: emailError,
      otherSpecifyError:
        clientRelationInformation.selectedDiscretion?.value === 'Other' && clientRelationInformation.otherSpecify === ''
          ? '*REQUIRED'
          : '',
    });

    if (
      !clientRelationInformation.relationFirstName ||
      !clientRelationInformation.relationLastName ||
      !clientRelationInformation.relationship ||
      (clientRelationInformation.relationMobile && !/^\d{10}$/.test(clientRelationInformation.relationMobile)) ||
      clientRelationError.emailError ||
      (clientRelationInformation.selectedDiscretion?.value === 'Other' && !clientRelationInformation.otherSpecify)
    ) {
      return;
    }

    if (
      clientRelationInformation.isMainContact &&
      relations.some((rel) => rel.mainContact && rel.email !== relation?.email)
    ) {
      setShowConfirmationModal(true);
      return;
    }

    saveRelation();
    closeModal();
  };

  const saveRelation = () => {
    let whatToSayWhenWeCall;
    if (clientRelationInformation.selectedDiscretion !== 'Other') {
      whatToSayWhenWeCall = clientRelationInformation.selectedDiscretion;
    } else {
      whatToSayWhenWeCall = clientRelationInformation.otherSpecify;
    }

    const newRelation = {
      ...relation,
      firstName: clientRelationInformation.relationFirstName,
      lastName: clientRelationInformation.relationLastName,
      relationship: clientRelationInformation.relationship,
      mobile: clientRelationInformation.relationMobile,
      email: clientRelationInformation.relationEmail,
      mainContact: clientRelationInformation.isMainContact,
      legalGuardian: clientRelationInformation.isLegalGuardian,
      mobileNumberVerified: clientRelationInformation.relationMobileNumberVerified,
      emailAddressVerified: clientRelationInformation.emailVerified,
      whatToSayWhenWeCall,
    };

    const copyOfRelations = structuredClone(relations);

    if (changeMainContact.current) {
      copyOfRelations.forEach((rel) => {
        rel.mainContact = false;
      });
    } else if (changeMainContact.current === false) {
      newRelation.mainContact = false;
    }

    const updatedRelations = [...copyOfRelations.filter((rel) => rel.email !== relation?.email), newRelation];
    updateSearchResults(updatedRelations);
  };

  return (
    <div className="tab-container client-relations">
      {showConfirmationModal && (
        <Modal
          closeModal={() => setShowConfirmationModal(false)}
          setIsModalOpen={showConfirmationModal}
          headerTitle={'Add Relation'}
          width={'500px'}
          height={'200px'}
          contentPadding={'10px'}
          justifyContent={'center'}
          hideButton={true}
        >
          <div className="client-relations-modal-message-container">
            <p>A main contact relation already exists, do you want to override it with this one?</p>
            <div className="client-relations-modal-buttons">
              <SaveButton color="#6F8594" title="No" onClick={handleConfirmationModalNo} />
              <SaveButton color="#6F8594" title="Yes" onClick={handleConfirmationModalYes} />
            </div>
          </div>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          closeModal={() => setShowDeleteModal(false)}
          setIsModalOpen={showConfirmationModal}
          headerTitle="Delete Relation"
          width="500px"
          height="175px"
          contentPadding="10px"
          justifyContent="center"
          hideButton
        >
          <div className="client-relations-modal-message-container">
            <p>Are you sure you want to delete this relation?</p>
            <div className="client-relations-modal-buttons">
              <SaveButton
                color="#FFFFFF"
                textColor="#6F8594"
                border="1px solid #6F8594"
                title="Cancel"
                onClick={() => setShowDeleteModal(false)}
              />
              <SaveButton color="#6F8594" border="1px solid #6F8594" title="Delete" onClick={handleDeleteClick} />
            </div>
          </div>
        </Modal>
      )}

      <div className="relations-section">
        {clientRelationInformation.isClientMinor && (
          <div className="input-container">
            <label className={'input-label'}>Legal Guardian / Parent?</label>
            <Checkbox
              color={'#000000'}
              label={'Yes'}
              fillStyle={true}
              onChange={() => handleClientRelationInformationChange('isLegalGuardian', true)}
              disabled={fieldsDisabled}
              checked={clientRelationInformation.isLegalGuardian}
            />
            <Checkbox
              color={'#000000'}
              label={'No'}
              fillStyle={true}
              onChange={() => handleClientRelationInformationChange('isLegalGuardian', false)}
              disabled={fieldsDisabled}
              checked={!clientRelationInformation.isLegalGuardian}
            />
          </div>
        )}

        <div className="input-container">
          <label className={'input-label'}>Main Contact?</label>
          <Checkbox
            color={'#000000'}
            label={'Yes'}
            fillStyle={true}
            onChange={() => handleClientRelationInformationChange('isMainContact', true)}
            disabled={fieldsDisabled}
            checked={clientRelationInformation.isMainContact}
          />
          <Checkbox
            color={'#000000'}
            label={'No'}
            fillStyle={true}
            onChange={() => handleClientRelationInformationChange('isMainContact', false)}
            disabled={fieldsDisabled}
            checked={!clientRelationInformation.isMainContact}
          />
        </div>
        <div className="input-container">
          <label className={'input-label'}>First Name:</label>
          <input
            className={`text-input ${clientRelationError.firstNameError && 'error-border'}`}
            type="text"
            value={clientRelationInformation.relationFirstName}
            disabled={fieldsDisabled}
            onChange={({ target }) => {
              handleClientRelationInformationChange('relationFirstName', target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'clientNextOfKinFirstName123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Relation First Name:'}
            />
          )}
        </div>
        <div className="error-message">
          {clientRelationError.firstNameError && (
            <div className="small-red-text">{clientRelationError.firstNameError}</div>
          )}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Last Name:</label>
          <input
            className={`text-input ${clientRelationError.lastNameError && 'error-border'}`}
            type="text"
            value={clientRelationInformation.relationLastName}
            disabled={fieldsDisabled}
            onChange={({ target }) => {
              handleClientRelationInformationChange('relationLastName', target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'clientNextOfKinLastName123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Relation Last Name:'}
            />
          )}
        </div>
        <div className="error-message">
          {clientRelationError.lastNameError && (
            <div className="small-red-text">{clientRelationError.lastNameError}</div>
          )}
        </div>

        <div className="input-container">
          <label className={'input-label'}>Relationship:</label>
          <input
            className={`text-input ${clientRelationError.lastNameError && 'error-border'}`}
            type="text"
            value={clientRelationInformation.relationship}
            disabled={fieldsDisabled}
            onChange={({ target }) => handleClientRelationInformationChange('relationship', target.value)}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'clientNextOfKinRelationship123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Relationship:'}
            />
          )}
        </div>
        <div className="error-message">
          {clientRelationError.relationshipError && (
            <div className="small-red-text">{clientRelationError.relationshipError}</div>
          )}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Mobile Number:</label>
          <input
            className={`text-input ${clientRelationError.mobileNumberError && 'error-border'}`}
            type="text"
            value={clientRelationInformation.relationMobile}
            disabled={disableMobileInput || fieldsDisabled}
            onChange={({ target }) => handleClientRelationInformationChange('relationMobile', target.value)}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'clientNextOfKinMobileNumber123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Relation Mobile Number:'}
            />
          )}
        </div>
        <div className="error-message">
          {clientRelationError.mobileNumberError && (
            <div className="small-red-text">{clientRelationError.mobileNumberError}</div>
          )}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Verified:</label>
          <Checkbox
            color={'#000000'}
            label={'Yes'}
            checked={clientRelationInformation.relationMobileNumberVerified}
            fillStyle={'fill'}
            disabled={fieldsDisabled}
            onChange={() => handleClientRelationInformationChange('relationMobileNumberVerified', true)}
          />
          <Checkbox
            color={'#000000'}
            label={'No'}
            checked={!clientRelationInformation.relationMobileNumberVerified}
            fillStyle={'fill'}
            disabled={fieldsDisabled}
            onChange={() => handleClientRelationInformationChange('relationMobileNumberVerified', false)}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'relationNumberVerified123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Relation Mobile Verified:'}
            />
          )}
        </div>
        <div className="input-container">
          <label className={'input-label'}>What must we say when we call?</label>
          <Select
            options={discretionList.current.map((origin) => ({
              value: origin,
              label: origin,
            }))}
            closeMenuOnSelect={true}
            onChange={(option) => handleClientRelationInformationChange('selectedDiscretion', option.value)}
            value={{
              value: clientRelationInformation.selectedDiscretion,
              label: clientRelationInformation.selectedDiscretion,
            }}
            isDisabled={fieldsDisabled}
            classNamePrefix="input-select"
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'discretionOption123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'What must we say when we call?'}
            />
          )}
        </div>
        {clientRelationInformation.selectedDiscretion === 'Other' && (
          <div>
            <div className="input-container">
              <label className={'input-label'}>Please Specify:</label>
              <input
                className={`text-input ${clientRelationError.otherSpecifyError && 'error-border'}`}
                type="text"
                value={clientRelationInformation.otherSpecify}
                disabled={fieldsDisabled}
                onChange={({ target }) => handleClientRelationInformationChange('otherSpecify', target.value)}
              />
            </div>
            <div className="error-message">
              {clientRelationError.otherSpecifyError && (
                <div className="small-red-text">{clientRelationError.otherSpecifyError}</div>
              )}
            </div>
          </div>
        )}
        <div className="input-container">
          <label className={'input-label'}>E-mail Address:</label>
          <input
            className={`text-input ${clientRelationError.emailError && 'error-border'}`}
            type="text"
            value={clientRelationInformation.relationEmail}
            disabled={fieldsDisabled}
            onChange={({ target }) => handleClientRelationInformationChange('relationEmail', target.value)}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'clientNextOfKinEmailAddress123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Relation E-mail Address:'}
            />
          )}
        </div>
        <div className="error-message">
          {clientRelationError.emailError && <div className="small-red-text">{clientRelationError.emailError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Verified:</label>
          <Checkbox
            color={'#000000'}
            label={'Yes'}
            checked={clientRelationInformation.emailVerified}
            fillStyle={'fill'}
            disabled={fieldsDisabled}
            onChange={() => handleClientRelationInformationChange('emailVerified', true)}
          />
          <Checkbox
            color={'#000000'}
            label={'No'}
            checked={!clientRelationInformation.emailVerified}
            fillStyle={'fill'}
            disabled={fieldsDisabled}
            onChange={() => handleClientRelationInformationChange('emailVerified', false)}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'relationNumberVerified123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Relation Email Verified:'}
            />
          )}
        </div>
      </div>
      <div className="relation-button-container">
        {relation && !disableMobileInput && (
          <SaveButton
            color="#FFFFFF"
            textColor="#6F8594FF"
            border="solid 1px #6F8594FF"
            title="Delete"
            disabled={fieldsDisabled}
            onClick={() => setShowDeleteModal(true)}
          />
        )}
        <SaveButton
          color="#6F8594FF"
          border="solid 1px #6F8594FF"
          title={relation ? 'Save' : 'Add'}
          disabled={fieldsDisabled}
          onClick={handleSaveRelationInModal}
        />
      </div>
    </div>
  );
};
